<template>
  <v-card>
    <v-card-text>
      <div ref="markerContainerRef" class="marker-container">
        <div class="timeline-elements-layer">
          <div
              v-for="element in timelineElements"
              :key="element.id">
            <v-tooltip class="tooltip" bottom :disabled="!element.tooltip">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs"
                     :style="getElementStyle(element)"
                     :class="getElementTypeClass(element)"
                     class="element-wrapper">
                  <div v-if="element.icon && element.type === 'icon'" :style="getIconStyle(element)">
                    <v-icon x-small :color="element.iconColor">
                      {{ element.icon }}
                    </v-icon>
                    <div :style="getLineStyle(element)" class="line"></div>
                  </div>

                  <v-icon
                      v-if="element.type === 'range' && element.icon"
                      :style="{ color: element.iconColor || 'white' }"
                      class="block-icon"
                      small
                  >
                    {{ element.icon }}
                  </v-icon>
                  <v-tooltip class="tooltip" bottom v-if="element.type === 'current-time'">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-on="on" v-bind="attrs">
                        <div :style="getLineStyle(element)" class="line"></div>
                        <div :style="getIconStyle(element)"></div>
                      </div>
                    </template>
                    {{ currentTime }}
                  </v-tooltip>
                </div>
              </template>
              {{ element.tooltip }}
            </v-tooltip>
          </div>
        </div>

        <div class="timeline-markers-layer">
          <div
              v-for="marker in visibleMarkers"
              :key="marker.uniqueKey"
              :style="getMarkerStyle(marker)"
              class="marker-wrapper text-center"
          >
            <div class="marker-line">|</div>
            <div class="marker-label">{{ marker.label }}</div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ChargingPlanVisualisation', // Optional: Add a component name
  props: ['smartChargingStatus', 'smartChargingPolicy'],
  data() {
    return {
      // --- Configuration ---
      startDate: 0,
      endDate: 24,
      minWidthPerMarker: 65, // Min width needed per marker label/line

      // --- Component state ---
      containerWidth: 0,
      resizeObserver: null,
      // Store the current Date object
      now: new Date(),
      // Store the interval
      timer: null,
      timelineElements: [],
    };
  },
  computed: {
    planEnded() {
      return this.smartChargingStatus?.plan?.endedAt
    },
    timelineWidth() {
      if (this.visibleMarkers.length < 1)
        return 0;
      return this.visibleMarkers[this.visibleMarkers?.length - 1].position - this.visibleMarkers[0]?.position
    },
    firstMarkerDate() {
      if (this.visibleMarkers.length < 1)
        return null;
      let date = new Date(this.startDate.getTime())
      date.setHours(this.visibleMarkers[0]?.value)
      if (date > this.startDate) {
        date.setDate(date.getDate() - 1);
      }
      return date;
    },
    lastMarkerDate() {
      if (this.visibleMarkers.length < 1)
        return null;
      let date = new Date(this.endDate.getTime())
      date.setHours(this.visibleMarkers[this.visibleMarkers?.length - 1]?.value)
      if (date < this.endDate) {
        date.setDate(date.getDate() + 1);
      }
      return date;
    },
    stepSize() {
      return Math.floor(this.timeDiff / this.maxMarkers) + 1;
    },
    timeDiffWithoutPadding() {
      // Get the difference in milliseconds
      const diffInMilliseconds = this.endDate.getTime() - this.startDate.getTime();
      // Convert milliseconds to hours

      return diffInMilliseconds / (1000 * 60 * 60);
    },
    timeDiff() {
      let timeDiffInHours = this.timeDiffWithoutPadding;
      // Add Padding
      timeDiffInHours += 2;
      let stepSize = Math.floor(timeDiffInHours / this.maxMarkers);
      timeDiffInHours += stepSize + 1
      return timeDiffInHours;

    },
    markerAmount() {
      this.calculateEndAndStartForMarkers()
      let timeDiff = this.calculateHoursBetweenDates(this.startDate, this.endDate)
      // Add Padding
      timeDiff += 2;
      let stepSize = Math.floor(timeDiff / this.maxMarkers);
      timeDiff += stepSize + 1
      if (timeDiff % this.maxMarkers === 0) {
        return this.maxMarkers
      }
      stepSize = Math.floor(timeDiff / this.maxMarkers);
      let markerAmount = this.maxMarkers - 1;
      let bestMarkerAmount = this.maxMarkers
      let bestPadding = this.maxMarkers - timeDiff % this.maxMarkers
      while (markerAmount > 1 && Math.floor(timeDiff / markerAmount) === stepSize) {
        let padding = markerAmount - timeDiff % markerAmount
        if (padding < bestPadding) {
          bestPadding = padding;
          bestMarkerAmount = markerAmount;
        }
        markerAmount--;
      }
      return bestMarkerAmount;
    },
    maxMarkers() {
      // Calculate max markers based on container width and minimum required width
      if (!this.containerWidth || this.containerWidth < this.minWidthPerMarker) return 0;
      return Math.max(1, Math.floor(this.containerWidth / this.minWidthPerMarker));
    },
    visibleMarkers() {
      // Calculate which markers should be visible based on maxMarkers and consistent steps
      if (!this.containerWidth || this.containerWidth < this.minWidthPerMarker) {
        return [];
      }
      let visible = [];
      let padding = this.timeDiff - this.timeDiffWithoutPadding;
      padding = Math.floor(padding / 2);
      let startHour = this.startDate.getHours() - padding;
      startHour = startHour < 0 ? startHour + 24 : startHour;
      let endHour = startHour + this.stepSize * (this.markerAmount - 1);
      if (endHour % 24 === this.endDate.getHours()) {
        startHour += 1;
      }
      for (let i = 0; i < this.markerAmount; i++) {
        let markerHour = startHour + i * this.stepSize;
        markerHour = markerHour % 24;
        visible.push({
          value: markerHour,
          label: String(markerHour).padStart(2, '0'),
          uniqueKey: `marker-${i}`,
          position: this.containerWidth / this.markerAmount * (i) + this.containerWidth / this.markerAmount / 2
        })
      }

      return visible;
    },
    currentTime() {
      // Get hours and minutes from the 'now' data property
      const hours = String(this.now.getHours()).padStart(2, '0');
      const minutes = String(this.now.getMinutes()).padStart(2, '0');
      // Return the formatted string HH:MM
      return `${hours}:${minutes}`;
    }
  },
  methods: {
    updateTimelineElements() {
      let timelineElements = [];
      if (this.smartChargingStatus === null) return [];
      if (this.smartChargingStatus.smartOverride) {
        this.addSmartOverrideElement(timelineElements)
      }
      this.addDeadlineElement(timelineElements)
      this.addPausedChargingElement(timelineElements);
      this.addChargingWindowElement(timelineElements);
      console.log(this.lastMarkerDate)
      if (this.lastMarkerDate > this.now) {
        this.addCurrentTimeElement(timelineElements);
      }
      this.timelineElements = timelineElements;
    },
    addChargingWindowElement(timelineElements) {
      timelineElements.push({
        id: 'estimatedChargingWindowElement',
        type: 'range',
        startDate: this.formatTime(this.smartChargingStatus?.plan?.startAt),
        endDate: this.formatTime(this.smartChargingStatus?.plan?.estimatedFinishAt),
        color: '#9e8400',
        icon: 'bolt',
        iconColor: '#9e8400',
        style: 'outline',
        tooltip: this.$t('smart-charging-plan-visualisation.estimated-charging-window')
      })
      if (this.smartChargingStatus?.plan?.startConfirmedAt) {
        let formattedStart = this.formatTime(this.smartChargingStatus?.plan?.startConfirmedAt);
        let formattedEnd = this.smartChargingStatus?.plan?.endedAt ? this.formatTime(this.smartChargingStatus?.plan?.endedAt) : this.getCurrentTime();
        let type = this.smartChargingStatus?.plan?.endedAt ? 'range' : 'range-unfinished';
        timelineElements.push({
          id: 'smartChargingWindowElement',
          type: type,
          startDate: formattedStart,
          endDate: formattedEnd,
          color: 'green',
          icon: 'bolt',
          iconColor: 'white',
          style: 'block',
          tooltip: this.$t('smart-charging-plan-visualisation.smart-charging-window')
        })
      }
    },
    addPausedChargingElement(timelineElements) {
      if (this.smartChargingStatus?.plan?.stopConfirmedAt) {
        let formattedStart = this.formatTime(this.smartChargingStatus?.plan?.stopConfirmedAt);
        timelineElements.push({
          id: 'pausedChargingElement',
          type: 'icon',
          startDate: formattedStart,
          endDate: this.getCurrentTime(),
          color: 'gray',
          icon: 'pause',
          iconColor: 'gray',
          tooltip: this.$t('smart-charging-plan-visualisation.charging-paused')
        })
      }
    },
    addDeadlineElement(timelineElements) {
      if (this.planEnded) {
        return
      }
      let deadlineDate = new Date()
      let deadline = this.smartChargingPolicy.deadline;
      let deadlineElements = deadline.split(':');
      deadlineDate.setHours(parseInt(deadlineElements[0]), parseInt(deadlineElements[1]), 0);
      if (deadlineDate < this.now) {
        deadlineDate.setDate(deadlineDate.getDate() + 1);
      }
      timelineElements.push({
        id: 'deadlineElement',
        type: 'icon',
        startDate: deadlineDate,
        icon: 'flag',
        color: 'green',
        iconColor: 'green',
        tooltip: this.$t('smart-charging-plan-visualisation.deadline')
      })
    },
    addCurrentTimeElement(timelineElements) {
      timelineElements.push({
        type: 'current-time',
        color: 'lightblue',
        startDate: this.now,
      })
    },
    addSmartOverrideElement(timelineElements) {
      if (this.planEnded) {
        return
      }
      let override = this.smartChargingStatus.smartOverride;
      let formattedStart = this.formatTime(override.createdAt);
      let formattedEnd;
      if (!override.endedAt) {
        formattedEnd = this.getCurrentTime()
        timelineElements.push({
          id: 'smartOverrideElement',
          type: 'range-unfinished',
          startDate: formattedStart,
          endDate: formattedEnd,
          color: 'green',
          style: 'block',
          tooltip: this.$t('smart-charging-plan-visualisation.smart-override')
        })
        timelineElements.push(
            {
              id: 'smartOverrideElementIcon',
              type: 'icon',
              startDate: formattedEnd,
              icon: 'bolt',
              color: 'green',
              iconColor: 'white',
              style: 'filled'
            }
        )
      } else {
        formattedEnd = this.formatTime(override.endedAt);
        timelineElements.push({
          id: 'smartOverrideElement',
          type: 'range',
          startDate: formattedStart,
          endDate: formattedEnd,
          color: 'green',
          style: 'block',
          icon: 'bolt',
          iconColor: 'white',
          tooltip: this.$t('smart-charging-plan-visualisation.smart-override')
        })
      }

    },

    timeDiffBetweenDates(date1, date2) {
      return date2 - date1
    },
    calculatePosition(date) {
      let all = this.timeDiffBetweenDates(this.firstMarkerDate, this.lastMarkerDate)
      let timeDiffToDate = this.timeDiffBetweenDates(this.firstMarkerDate, date)
      return (timeDiffToDate / all * this.timelineWidth) + this.visibleMarkers[0]?.position;
    },

    calculateWidth(startDate, endDate) {
      let start = this.calculatePosition(startDate)
      let end = this.calculatePosition(endDate)
      return end - start
    },

    getElementTypeClass(element) {
      switch (element.type) {
        case 'range':
          return 'timeline-block';
        case 'range-unfinished':
          return 'timeline-block-unfinished';
        case 'icon':
          return 'timeline-element';
      }
    },
    // --- STYLE GETTERS ---
    getElementStyle(element) {
      // Generates the style object for a timeline element (icon or range)
      const style = {
        position: 'absolute',
        left: this.calculatePosition(element.startDate) + 'px',
        zIndex: 2, // Elements above markers
        whiteSpace: 'nowrap', // Prevent text wrapping inside elements
        boxSizing: 'border-box' // Explicitly set box-sizing
      };

      if (element.type === 'range') {
        style.top = '25px'; // Position ranges lower
        style.width = this.calculateWidth(element.startDate, element.endDate) + 'px';
        style.height = '20px'; // Height of the range block
        style.backgroundColor = element.style === 'block' ? element.color : 'transparent';
        style.border = element.style === 'outline' ? `1px solid ${element.color}` : 'none';
        style.borderRadius = '5px';
        style.zIndex = 3;
        // Centering of icon inside is handled by 'timeline-block' class
      } else if (element.type === 'range-unfinished') {
        style.top = '25px'; // Position ranges lower
        style.width = this.calculateWidth(element.startDate, element.endDate) + 'px';
        style.height = '20px'; // Height of the range block
        style.backgroundColor = element.style === 'block' ? element.color : 'transparent';
        style.border = element.style === 'outline' ? `1px solid ${element.color}` : 'none';
        style.borderRadius = '5px 0 0 5px';
        // Centering of icon inside is handled by 'timeline-block' class
      } else if (element.type === 'current-time') {
        style.zIndex = 4;
      } else {
        // Standalone icons ('mdi-icon', 'p-icon')
        // style.top = '0px'; // Position standalone icons higher
        style.transform = 'translateX(-50%)'; // Center icon horizontally over its time point
        style.color = element.color || 'inherit'; // Use element color if provided
      }
      return style;
    },

    getIconStyle(element) {
      switch (element.type) {
        case 'current-time':
          return {
            width: '0', // Standard icon size
            height: '0', // Standard icon size
            position: 'absolute', // Position the icon relative to its time point
            borderBottom: "5px solid " + element.color,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            transform: 'translateX(-50%)',
            top: '45px', // Position the icon relative to its time point
            opacity: '0.5', // Semi-transparent for current time marker
          }
        default:
          return {
            width: '20px', // Standard icon size
            height: '20px', // Standard icon size
            borderRadius: '50%', // Circle shape for icon
            display: 'inline-flex', // Center icon horizontally over its time point
            alignItems: 'center', // Center icon vertically
            justifyContent: 'center', // Center icon horizontally over its time point
            position: 'relative', // Position the icon relative to its time point
            marginBottom: '30px',
            boxSizing: 'border-box',
            border: "1px solid " + element.color
          }
      }
    },

    getLineStyle(element) {
      switch (element.type) {
        case 'current-time':
          return {
            content: '',
            position: 'absolute',
            left: '50%',
            /* Adjust bottom: Negative line height + half border width */
            bottom: '-46px', /* -20px (height) - 1px (half border width) */
            transform: 'translateX(-50%)',
            width: '1px', /* Line thickness */
            height: '21px', /* Line length */
            backgroundColor: element.color, /* Line color */
            opacity: '0.5', // Semi-transparent for current time marker
          }
        default:
          return {
            content: '',
            position: 'absolute',
            left: '50%',
            /* Adjust bottom: Negative line height + half border width */
            bottom: '-21px', /* -20px (height) - 1px (half border width) */
            transform: 'translateX(-50%)',
            width: '1px', /* Line thickness */
            height: '20px', /* Line length */
            backgroundColor: element.color /* Line color */
          }
      }
    },

    getMarkerStyle(marker) {
      // Generates the style object for a time marker
      return {
        position: 'absolute',
        left: marker.position + 'px', // Use the SAME calculation as elements
        top: '50px', // Position markers vertically (adjust as needed, e.g., below ranges)
        transform: 'translateX(-50%)', // Center the marker content horizontally
        zIndex: 1, // Ensure markers are below elements
        boxSizing: 'border-box' // Explicitly set box-sizing
      };
    },

    // --- RESIZE HANDLING ---
    setupResizeObserver() {
      // Observes the container width for responsive calculations
      if (this.resizeObserver) this.resizeObserver.disconnect(); // Clean up previous observer

      this.resizeObserver = new ResizeObserver(entries => {
        if (entries[0]) {
          const newWidth = entries[0].contentRect.width;
          // Avoid unnecessary updates if width hasn't changed significantly
          if (Math.abs(newWidth - this.containerWidth) > 0.5) {
            this.containerWidth = newWidth;
          }
        }
      });

      this.$nextTick(() => { // Ensure element exists in DOM
        const containerElement = this.$refs.markerContainerRef;
        if (containerElement) {
          // Set initial width immediately
          this.containerWidth = containerElement.clientWidth;
          this.resizeObserver.observe(containerElement);
        } else {
          console.error("Timeline container ref 'markerContainerRef' not found on mount!");
        }
      });
    },
    formatTime(timestamp) {
      return new Date(timestamp);
    },
    getCurrentTime() {
      return new Date();
    },
    updateCurrentTime() {
      this.now = new Date();
    },
    update() {
      this.updateCurrentTime()
      this.updateTimelineElements()
    },
    calculateEndAndStartForMarkers() {
      let possibleStartTimes = [
        this.smartChargingStatus?.plan?.startAt,
        this.smartChargingStatus?.smartOverride?.createdAt,
        this.smartChargingStatus?.plan?.startConfirmedAt,
        this.smartChargingStatus?.plan?.stopConfirmedAt,
      ]
      const filteredPossibleStartTimes = possibleStartTimes.filter(value => value !== null);
      let start = new Date()
      for (const filteredPossibleStartTimesKey in filteredPossibleStartTimes) {
        if (new Date(filteredPossibleStartTimes[filteredPossibleStartTimesKey]) < start) {
          start = new Date(filteredPossibleStartTimes[filteredPossibleStartTimesKey]);
        }
      }
      let end;
      if (this.smartChargingStatus?.plan?.endedAt) {
        end = new Date(this.smartChargingStatus?.plan?.endedAt);
      } else {
        let deadlineDate = new Date()
        let deadline = this.smartChargingPolicy.deadline;
        let deadlineElements = deadline.split(':');
        deadlineDate.setHours(parseInt(deadlineElements[0]), parseInt(deadlineElements[1]), 0);
        if (deadlineDate < this.now) {
          deadlineDate.setDate(deadlineDate.getDate() + 1);
        }
        let possibleEndTimes = [
          this.smartChargingStatus?.plan?.stopConfirmedAt,
          this.smartChargingStatus?.plan?.estimatedFinishAt,
          this.smartChargingStatus?.plan?.stopAt,
          deadlineDate
        ]
        const filteredPossibleEndTimes = possibleEndTimes.filter(value => value !== null);
        end = new Date()
        for (const filteredPossibleEndTimesKey in filteredPossibleEndTimes) {
          if (new Date(filteredPossibleEndTimes[filteredPossibleEndTimesKey]) > end) {
            end = new Date(filteredPossibleEndTimes[filteredPossibleEndTimesKey]);
          }
        }
      }
      start.setMinutes(0, 0)
      end.setMinutes(0, 0)
      end.setHours(end.getHours() + 1)
      this.startDate = start;
      this.endDate = end;
    },

    calculateHoursBetweenDates(date1, date2) {
      // Get the difference in milliseconds
      const diffInMilliseconds = date2.getTime() - date1.getTime();

      // Convert milliseconds to hours
      return diffInMilliseconds / (1000 * 60 * 60);
    }
  },
  // --- LIFECYCLE HOOKS ---
  mounted() {
    this.setupResizeObserver();
    this.calculateEndAndStartForMarkers()
    // 1. Update the time immediately when the component is mounted
    this.updateCurrentTime();
    this.updateTimelineElements()
    this.timer = setInterval(this.update, 10000);
  },
  watch: {
    lastMarkerDate(newVal, oldVal) {
      if (!oldVal) {
        this.updateTimelineElements()
      }
    }
  },
  beforeDestroy() {
    // Clean up the observer when the component is destroyed
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // 3. IMPORTANT: Clear the interval when the component is destroyed
    //    This prevents memory leaks and errors if the component is removed
    //    from the page while the interval is still running.
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
};
</script>

<style scoped>
@import "~@/styles/smart-charging-visualisation.scss";
</style>